<script>
import { defineComponent, computed, watch } from 'vue'
import { rationaleEditor } from '@rm-ui/levelling/rationaleEditor'
import { ATLAS_DRAFT_BETA_LABEL, ATLAS_DRAFT_DISCLAIMER } from '@rm-ui/levelling/rationaleDraft'
import { useRationaleDraft } from '@/composables/useRationaleDraft'

/**
 * Rationale editor with "Use Atlas" (PD-1301) — one block shared by the
 * evaluation (sub-factor) and classification (factor) questions, built to the
 * Claude Design prototype (lvl/wizard.jsx LvlSubQuestion):
 *
 *   add  — "Add rationale" link + "(optional)"
 *   edit — textarea · Use Atlas / Atlas is writing… / Try again · Cancel · Save,
 *          the "Drafted by Atlas · edit freely before you save" tag once a draft
 *          landed, a Keep mine / Replace confirm when typed text would be
 *          overwritten, and an error line with Try again that leaves typed text
 *          exactly as it was
 *   view — heading + pencil, provenance line, text, author · date
 *
 * The state machine is `@rm-ui/levelling/rationaleEditor`; persistence stays with
 * the parent: `update` carries { text, provenance } and the parent hands the
 * saved values back through `comment` / `provenance`.
 *
 * "Use Atlas" only renders when `canDraft` is true (the tenant has a
 * rationale_draft chain AND the journey's AI assist is on — PD-1246) and is
 * disabled with the reason as a tooltip until a score is selected.
 */
export default defineComponent({
  name: 'RationaleEditor',
  props: {
    comment: { type: String, default: null },
    provenance: { type: String, default: null },
    author: { type: String, default: null },
    date: { type: String, default: null },
    readOnly: { type: Boolean, default: false },
    optionalLabel: { type: String, default: '(optional)' },
    canDraft: { type: Boolean, default: false },
    draftDisabledReason: { type: String, default: null },
    /* The assist analysis for this question is still running: the button shows
       a spinner and "Analysing document…" instead of "Use Atlas". */
    draftPending: { type: Boolean, default: false },
    /* Visible line under the actions explaining why Atlas is unavailable
       (rationaleDraft.draftGateHint). Null = nothing to explain. */
    draftHint: { type: String, default: null },
    /* () => request for rationaleDraftRun.run — built by the parent at click
       time, since only it knows the factor, level and evidence in play. */
    draftRequest: { type: Function, default: null },
  },
  emits: ['update'],
  setup(props, { emit }) {
    const runner = useRationaleDraft()
    const editor = rationaleEditor({
      getSaved: () => ({ text: props.comment || '', provenance: props.provenance || null }),
      runner,
    })

    // The stored answer changed underneath (navigation, resume, parent save):
    // re-sync unless the evaluator is mid-edit.
    watch(() => [props.comment, props.provenance], () => {
      if (editor.mode.value !== 'edit') editor.syncFromSaved()
    })

    const draftDisabled = computed(() => editor.generating.value || !!props.draftDisabledReason)
    const draftTitle = computed(() => props.draftDisabledReason || null)
    const busy = computed(() => editor.generating.value || props.draftPending)
    const buttonLabel = computed(() => {
      if (editor.generating.value) return editor.atlasLabel.value
      if (props.draftPending) return 'Analysing document…'
      return editor.atlasLabel.value
    })

    function useAtlas() {
      if (!props.draftRequest || draftDisabled.value) return
      editor.useAtlas(props.draftRequest())
    }

    function save() {
      const out = editor.save()
      if (out) emit('update', out)
    }

    return {
      mode: editor.mode,
      text: editor.text,
      hasDraft: editor.hasDraft,
      askOver: editor.askOver,
      generating: editor.generating,
      draftError: editor.draftError,
      canSave: editor.canSave,
      atlasLabel: editor.atlasLabel,
      savedProvenanceLabel: editor.savedProvenanceLabel,
      startAdd: editor.startAdd,
      startEdit: editor.startEdit,
      cancel: editor.cancel,
      keepMine: editor.keepMine,
      confirmReplace: editor.confirmReplace,
      retry: editor.retry,
      draftDisabled,
      draftTitle,
      busy,
      buttonLabel,
      betaLabel: ATLAS_DRAFT_BETA_LABEL,
      disclaimer: ATLAS_DRAFT_DISCLAIMER,
      useAtlas,
      save,
    }
  }
})
</script>

<template>
  <div class="lvl-rat">
    <!-- Edit -->
    <div v-if="mode === 'edit'" class="lvl-q-rationale">
      <div v-if="hasDraft" class="lvl-rat-tools">
        <span class="lvl-aidraft-tag"><span class="ms">explore</span> Drafted by Atlas <span class="lvl-beta-pill">{{ betaLabel }}</span> <span class="lvl-aidraft-tag-sec">· edit freely before you save</span></span>
        <span class="lvl-aidraft-note">{{ disclaimer }}</span>
      </div>
      <div v-if="askOver" class="lvl-rat-confirm">
        <span>Replace what you have written with an Atlas draft?</span>
        <span class="lvl-rat-confirm-btns">
          <button type="button" @click="keepMine">Keep mine</button>
          <button type="button" class="is-go" @click="confirmReplace">Replace</button>
        </span>
      </div>
      <label for="evaluation-rationale">Rationale</label>
      <v-textarea
        id="evaluation-rationale"
        v-model="text"
        class="lvl-textarea"
        outlined
        hide-details
        auto-grow
        rows="4"
        :disabled="generating"
        placeholder="Explain your reasoning or add context…"
      ></v-textarea>
      <div v-if="draftError" class="lvl-rat-err">
        <span class="ms">error</span> {{ draftError }}
        <button type="button" @click="retry">Try again</button>
      </div>
      <div class="lvl-q-rationale-btns">
        <button
          v-if="canDraft"
          type="button"
          class="lvl-aidraft"
          :disabled="draftDisabled"
          :title="draftTitle"
          data-testid="rationale-use-atlas"
          @click="useAtlas"
        >
          <span class="ms" :class="{ 'lvl-spin': busy }">{{ busy ? 'progress_activity' : 'explore' }}</span>
          {{ buttonLabel }}
          <span class="lvl-beta-pill lvl-beta-pill--onfill">{{ betaLabel }}</span>
        </button>
        <button type="button" class="lvl-btn lvl-btn--outlined" @click="cancel">Cancel</button>
        <button type="button" class="lvl-btn" :disabled="!canSave" data-testid="rationale-save" @click="save">Save</button>
      </div>
      <div v-if="canDraft && draftHint" class="lvl-rat-hint" data-testid="rationale-atlas-hint">
        <span class="ms">{{ draftPending ? 'hourglass_top' : 'info' }}</span> {{ draftHint }}
      </div>
    </div>

    <!-- View -->
    <div v-else-if="mode === 'view'" class="lvl-q-rationale-view">
      <h5>
        <span>Rationale</span>
        <button v-if="!readOnly" type="button" aria-label="Edit rationale" @click="startEdit"><span class="ms">edit</span></button>
      </h5>
      <div v-if="savedProvenanceLabel" class="lvl-rat-by"><span class="ms">explore</span> {{ savedProvenanceLabel }}</div>
      <div class="rtxt">{{ comment }}</div>
      <div class="rationale-meta">
        <span>{{ author }}</span><span v-if="date"> · {{ date }}</span>
      </div>
    </div>

    <!-- Add -->
    <div v-else class="lvl-q-rationale-add">
      <button type="button" :disabled="readOnly" @click="startAdd"><span class="ms">edit</span> Add rationale</button>
      <span class="opt">{{ optionalLabel }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rationale-meta {
  margin-top: 8px;
  font-size: 11px;
  color: var(--v3-text-3);
}
</style>
